import React, { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Tent, AlertTriangle, Send, ArrowLeft } from 'lucide-react';

const SiteReportForm = () => {
    const { camp_location_id, userId } = useParams(); // userId should be XenForo user_id
    const [formData, setFormData] = useState({
        subject: '',
        message: '',
        category: '',
        priority: 'medium'
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    
    // Get site name from URL parameters
    const siteName = searchParams.get('site_name') || `Site ID: ${camp_location_id}`;

    const categoryOptions = [
        'Road Conditions',
        'Site Accessibility',
        'Facilities Issue',
        'Safety Concern',
        'Environmental Impact',
        'Maintenance Needed',
        'Information Update',
        'Other'
    ];

    const priorityOptions = [
        { value: 'low', label: 'Low', color: 'text-gray-600' },
        { value: 'medium', label: 'Medium', color: 'text-blue-600' },
        { value: 'high', label: 'High', color: 'text-orange-600' },
        { value: 'urgent', label: 'Urgent', color: 'text-red-600' }
    ];

    const validateForm = () => {
        const newErrors = {};

        if (!formData.subject.trim()) {
            newErrors.subject = 'Subject is required';
        } else if (formData.subject.length > 255) {
            newErrors.subject = 'Subject must be 255 characters or less';
        }

        if (!formData.message.trim()) {
            newErrors.message = 'Message is required';
        } else if (formData.message.length < 10) {
            newErrors.message = 'Message must be at least 10 characters';
        }

        if (!formData.category) {
            newErrors.category = 'Category is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;

        setIsSubmitting(true);


        let siteId = parseInt(camp_location_id);
        try {
            const submitData = {
                ...formData,
                site_location:  siteId
            };

            const response = await fetch('/otgservices/submitSiteReport.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(submitData),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to submit report');
            }

            const result = await response.json();

            if (result.success) {
                setSubmitted(true);
                // Clear form
                setFormData({
                    subject: '',
                    message: '',
                    category: '',
                    priority: 'medium'
                });
            } else {
                throw new Error(result.message || 'Failed to submit report');
            }

        } catch (error) {
            console.error('Error submitting report:', error);
            alert('Error submitting report: ' + error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleBack = () => {
        navigate(-1);
    };

    if (submitted) {
        return (
            <div className="min-h-screen bg-gray-50">
                {/* Header */}
                <div className="bg-orange-600 text-white px-6 py-4">
                    <div className="max-w-7xl mx-auto flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="bg-white p-2 rounded">
                                <Tent className="w-6 h-6 text-orange-600" />
                            </div>
                            <div>
                                <h1 className="text-xl font-bold">OTG Camp</h1>
                                <p className="text-sm opacity-90">SITE REPORT</p>
                            </div>
                        </div>
                        <div className="text-right">
                            <span className="text-sm bg-orange-700 px-2 py-1 rounded">Pro</span>
                        </div>
                    </div>
                </div>

                <div className="max-w-2xl mx-auto p-6">
                    <div className="bg-white rounded-lg shadow-lg p-8 text-center">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Send className="w-8 h-8 text-green-600" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Report Submitted Successfully!</h2>
                        <p className="text-gray-600 mb-6">
                            Thank you for reporting issues with <strong>{siteName}</strong>. 
                            Your report has been received and will be reviewed by our team.
                        </p>
                        <div className="flex gap-3 justify-center">
                            <button
                                onClick={() => setSubmitted(false)}
                                className="px-6 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
                            >
                                Submit Another Report
                            </button>
                            <button
                                onClick={handleBack}
                                className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
                            >
                                Go Back
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-orange-600 text-white px-6 py-4">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="bg-white p-2 rounded">
                            <Tent className="w-6 h-6 text-orange-600" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold">OTG Camp</h1>
                            <p className="text-sm opacity-90">SITE REPORT</p>
                        </div>
                    </div>
                    <div className="text-right">
                        <span className="text-sm bg-orange-700 px-2 py-1 rounded">Pro</span>
                    </div>
                </div>
            </div>

            <div className="max-w-2xl mx-auto p-6">
                <div className="bg-white rounded-lg shadow-lg p-6">
                    {/* Back Button */}
                    <button
                        onClick={handleBack}
                        className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back
                    </button>

                    {/* Header */}
                    <div className="flex items-center gap-3 mb-6">
                        <div className="bg-orange-100 p-3 rounded-lg">
                            <AlertTriangle className="w-6 h-6 text-orange-600" />
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold text-gray-800">Report Site Issue</h2>
                            <p className="text-gray-600">
                                Reporting for: <span className="font-medium">{siteName}</span>
                            </p>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Category */}
                        <div>
                            <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                                Category *
                            </label>
                            <select
                                id="category"
                                name="category"
                                value={formData.category}
                                onChange={handleChange}
                                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                                    errors.category ? 'border-red-500' : 'border-gray-300'
                                }`}
                            >
                                <option value="">Select a category</option>
                                {categoryOptions.map(option => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </select>
                            {errors.category && <p className="mt-1 text-sm text-red-600">{errors.category}</p>}
                        </div>

                        {/* Priority */}
                        <div>
                            <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-1">
                                Priority
                            </label>
                            <select
                                id="priority"
                                name="priority"
                                value={formData.priority}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                            >
                                {priorityOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Subject */}
                        <div>
                            <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                                Subject *
                            </label>
                            <input
                                type="text"
                                id="subject"
                                name="subject"
                                value={formData.subject}
                                onChange={handleChange}
                                maxLength="255"
                                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                                    errors.subject ? 'border-red-500' : 'border-gray-300'
                                }`}
                                placeholder="Brief description of the issue"
                            />
                            {errors.subject && <p className="mt-1 text-sm text-red-600">{errors.subject}</p>}
                            <p className="mt-1 text-sm text-gray-500">{formData.subject.length}/255 characters</p>
                        </div>

                        {/* Message */}
                        <div>
                            <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                                Message *
                            </label>
                            <textarea
                                id="message"
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                rows={6}
                                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                                    errors.message ? 'border-red-500' : 'border-gray-300'
                                }`}
                                placeholder="Provide detailed information about the issue, including current conditions, accessibility concerns, or any other relevant details..."
                            />
                            {errors.message && <p className="mt-1 text-sm text-red-600">{errors.message}</p>}
                            <p className="mt-1 text-sm text-gray-500">
                                {formData.message.length} characters (minimum 10)
                            </p>
                        </div>

                        {/* Submit Buttons */}
                        <div className="flex gap-3 pt-4">
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-md text-white font-medium ${
                                    isSubmitting
                                        ? 'bg-gray-400 cursor-not-allowed'
                                        : 'bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500'
                                }`}
                            >
                                {isSubmitting ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                        Submitting...
                                    </>
                                ) : (
                                    <>
                                        <Send className="w-4 h-4" />
                                        Submit Report
                                    </>
                                )}
                            </button>

                            <button
                                type="button"
                                onClick={handleBack}
                                disabled={isSubmitting}
                                className="px-6 py-3 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default SiteReportForm;